import { useRef, useState } from "react";
import { FolderOpen, Loader2, Upload } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import MediaPicker from "./MediaPicker";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

export default function ImageField({ label, value, onChange, kind = "image", testid }) {
  const [uploading, setUploading] = useState(false);
  const [picker, setPicker] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("kind", kind);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.path);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((Array.isArray(detail) ? detail[0]?.msg : detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const accept = kind === "video" ? "video/mp4,video/webm" : "image/jpeg,image/png,image/webp";

  return (
    <div data-testid={testid}>
      <span className={labelCls}>{label}</span>
      <div className="flex items-start gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-cream text-[9px] text-[#5C5C5C]">
          {value ? (
            kind === "video" ? (
              <video src={imgUrl(value)} className="h-full w-full object-cover" muted />
            ) : (
              <img src={imgUrl(value)} alt="" className="h-full w-full object-cover" />
            )
          ) : (
            "None"
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <input
            data-testid={`${testid}-input`}
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Upload, pick from Media, or paste URL"
            className={inputCls}
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={`${testid}-upload`}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-rose hover:text-rose disabled:opacity-60"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
            </button>
            <button
              type="button"
              data-testid={`${testid}-media`}
              onClick={() => setPicker(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-rose hover:text-rose"
            >
              <FolderOpen size={13} /> Media
            </button>
            <input ref={fileRef} type="file" accept={accept} onChange={upload} className="hidden" />
          </div>
          {error && <p className="text-[11px] font-medium text-red-500">{error}</p>}
        </div>
      </div>
      {picker && (
        <MediaPicker kind={kind} onClose={() => setPicker(false)} onSelect={(path) => { onChange(path); setPicker(false); }} />
      )}
    </div>
  );
}
